import React from 'react';
import { DataCollectionGapSummaryMaterial } from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { useLocalizer } from '../hooks/useLocalizer';
import { MaterialPageSize } from './helpers/gapSummaryHelpers';
import MaterialGapRow from './MaterialGapRow';

interface MaterialUnansweredQuestionsListProps {
    materialGaps: DataCollectionGapSummaryMaterial[];
}

export const MaterialUnansweredQuestionsList: React.FC<MaterialUnansweredQuestionsListProps> = ({
    materialGaps
}) => {
    const localize = useLocalizer();
    const [expandedMaterialIds, setExpandedMaterialIds] = React.useState<Set<string>>(new Set());
    const [materialPageIndex, setMaterialPageIndex] = React.useState(0);
    const materialPageCount = Math.max(Math.ceil(materialGaps.length / MaterialPageSize), 1);

    const pagedMaterialGaps = React.useMemo(() => {
        const startIndex = materialPageIndex * MaterialPageSize;
        return materialGaps.slice(startIndex, startIndex + MaterialPageSize);
    }, [materialGaps, materialPageIndex]);

    React.useEffect(() => {
        if (materialPageIndex >= materialPageCount) {
            setMaterialPageIndex(materialPageCount - 1);
        }
    }, [materialPageCount, materialPageIndex]);

    const toggleMaterial = (inventoryId: string) => {
        setExpandedMaterialIds(current => {
            const next = new Set(current);
            if (next.has(inventoryId)) {
                next.delete(inventoryId);
            } else {
                next.add(inventoryId);
            }

            return next;
        });
    };

    return (
        <>
            {pagedMaterialGaps.map(material => (
                <MaterialGapRow
                    key={material.inventoryId}
                    material={material}
                    isExpanded={expandedMaterialIds.has(material.inventoryId)}
                    onToggle={toggleMaterial}
                />
            ))}
            {materialGaps.length > MaterialPageSize && (
                <div className="gap-summary-pagination">
                    <span className="gap-summary-pagination-range">
                        {localize('Showing')} {materialPageIndex * MaterialPageSize + 1}-{Math.min((materialPageIndex + 1) * MaterialPageSize, materialGaps.length)} {localize('of')} {materialGaps.length} {localize('materials')}
                    </span>
                    <button
                        type="button"
                        className="btn btn-link"
                        disabled={materialPageIndex === 0}
                        onClick={() => setMaterialPageIndex(Math.max(materialPageIndex - 1, 0))}
                    >
                        <span className="glyphicon glyphicon-chevron-left" aria-hidden="true"></span>
                        {' '}
                        {localize('Previous')}
                    </button>
                    {Array.from({ length: materialPageCount }, (_, pageIndex) => (
                        <button
                            type="button"
                            key={pageIndex}
                            className={`gap-summary-page-button${materialPageIndex === pageIndex ? ' active' : ''}`}
                            onClick={() => setMaterialPageIndex(pageIndex)}
                        >
                            {pageIndex + 1}
                        </button>
                    ))}
                    <button
                        type="button"
                        className="btn btn-link"
                        disabled={materialPageIndex >= materialPageCount - 1}
                        onClick={() => setMaterialPageIndex(Math.min(materialPageIndex + 1, materialPageCount - 1))}
                    >
                        {localize('Next')}
                        {' '}
                        <span className="glyphicon glyphicon-chevron-right" aria-hidden="true"></span>
                    </button>
                </div>
            )}
        </>
    );
};

export default MaterialUnansweredQuestionsList;
